import React, { useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Skeleton
} from '@mui/material'
import {
  Assessment,
  TrendingUp,
  PieChart,
  BarChart,
  Timeline,
  Download,
  Refresh
} from '@mui/icons-material'

import ReportFilters from '../../components/Reports/ReportFilters'
import AgingReport from '../../components/Reports/AgingReport'
import SalesChart from '../../components/Dashboard/SalesChart'
import TopDebtorsChart from '../../components/Dashboard/TopDebtorsChart'
import { 
  useAccountStatusReport, 
  useMonthlySalesReport, 
  useTopDebtorsReport,
  useAgingReport 
} from '../../hooks/useReports'
import { formatCurrency, formatDate, formatInvoiceStatus } from '../../utils/formatters'

const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    status: '',
    clientId: ''
  })

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters)
  }

  const handleGenerateReport = () => {
    // Force refetch of current report
    switch (activeTab) {
      case 0:
        accountStatusRefetch()
        break
      case 1:
        monthlySalesRefetch()
        break
      case 2:
        topDebtorsRefetch()
        break
      case 3:
        agingRefetch()
        break
    }
  }

  const handleExportReport = () => {
    // TODO: Implement export functionality
    console.log('Export report:', activeTab, filters)
  }

  // Report queries
  const { 
    data: accountStatusData, 
    isLoading: accountStatusLoading, 
    error: accountStatusError,
    refetch: accountStatusRefetch 
  } = useAccountStatusReport(filters)

  const { 
    data: monthlySalesData, 
    isLoading: monthlySalesLoading,
    refetch: monthlySalesRefetch 
  } = useMonthlySalesReport({ 
    year: new Date().getFullYear(),
    startDate: filters.startDate,
    endDate: filters.endDate
  })

  const { 
    data: topDebtorsData, 
    isLoading: topDebtorsLoading,
    refetch: topDebtorsRefetch 
  } = useTopDebtorsReport({ limit: 10 })

  const { 
    refetch: agingRefetch 
  } = useAgingReport({})

  const reportTabs = [
    {
      label: 'Estado de Cuenta',
      icon: <Assessment />,
      color: 'primary'
    },
    {
      label: 'Ventas Mensuales',
      icon: <TrendingUp />,
      color: 'success'
    },
    {
      label: 'Top Deudores',
      icon: <PieChart />,
      color: 'warning'
    },
    {
      label: 'Antigüedad',
      icon: <BarChart />,
      color: 'info'
    }
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <AccountStatusReport data={accountStatusData} loading={accountStatusLoading} error={accountStatusError} />
      case 1:
        return <MonthlySalesReport data={monthlySalesData} loading={monthlySalesLoading} />
      case 2:
        return <TopDebtorsReport data={topDebtorsData} loading={topDebtorsLoading} />
      case 3:
        return <AgingReport filters={filters} />
      default:
        return null
    }
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Reportes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Genera y visualiza reportes detallados del negocio
        </Typography>
      </Box>

      {/* Report Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {reportTabs.map((tab, index) => (
            <Tab 
              key={index}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
          ))}
        </Tabs>
      </Card>

      {/* Filters */}
      <ReportFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onGenerate={handleGenerateReport}
        onClear={() => setFilters({ startDate: null, endDate: null, status: '', clientId: '' })}
        onExport={handleExportReport}
        loading={accountStatusLoading}
        showClientFilter={activeTab === 0}
        showStatusFilter={activeTab === 0}
        showDateFilters={activeTab === 0 || activeTab === 1}
        title={`Filtros - ${reportTabs[activeTab]?.label}`}
      />

      {/* Report Content */}
      {renderTabContent()}
    </Box>
  )
}

// Account Status Report Component
const AccountStatusReport = ({ data, loading, error }) => {
  if (error) {
    return (
      <Alert severity="error">
        Error cargando reporte: {error.message}
      </Alert>
    )
  }

  if (loading) {
    return <Skeleton variant="rectangular" height={400} />
  }

  if (!data) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Configura los filtros y haz clic en "Actualizar" para generar el reporte
        </Typography>
      </Paper>
    )
  }

  const summary = data.summary || {}

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="primary.main">
                {summary.total?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Facturas
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatCurrency(summary.total?.amount || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {summary.paid?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pagadas
              </Typography>
              <Typography variant="h6" fontWeight={600} color="success.main">
                {formatCurrency(summary.paid?.amount || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {summary.pending?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Por Cobrar
              </Typography>
              <Typography variant="h6" fontWeight={600} color="warning.main">
                {formatCurrency(summary.pending?.amount || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="error.main">
                {summary.overdue?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vencidas
              </Typography>
              <Typography variant="h6" fontWeight={600} color="error.main">
                {formatCurrency(summary.overdue?.amount || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Invoices Table */}
      {data.invoices && data.invoices.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Facturas Filtradas ({data.invoices.length})
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Factura</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Emisión</TableCell>
                    <TableCell>Vencimiento</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell align="center">Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.invoices.slice(0, 20).map((invoice) => (
                    <TableRow key={invoice.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {invoice.invoiceNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.client?.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(invoice.issueDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(invoice.dueDate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(invoice.totalAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={formatInvoiceStatus(invoice.visualStatus || invoice.status)}
                          color={
                            invoice.status === 'PAGADA' ? 'success' :
                            (invoice.visualStatus || invoice.status) === 'VENCIDA' ? 'error' : 'warning'
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {data.invoices.length > 20 && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Mostrando 20 de {data.invoices.length} facturas
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

// Monthly Sales Report Component
const MonthlySalesReport = ({ data, loading }) => {
  if (loading) {
    return <Skeleton variant="rectangular" height={400} />
  }

  if (!data) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Cargando reporte de ventas mensuales...
        </Typography>
      </Paper>
    )
  }

  return (
    <Box>
      {/* Summary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="primary.main">
                {data.totals?.invoiceCount || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Facturas Año
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {formatCurrency(data.totals?.totalAmount || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Facturado
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {formatCurrency(data.totals?.pendingAmount || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pendiente
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="error.main">
                {formatCurrency(data.totals?.overdueAmount || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vencido
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chart */}
      <SalesChart 
        data={data.monthlySales || []}
        loading={loading}
        title={`Ventas Mensuales ${data.year || new Date().getFullYear()}`}
      />
    </Box>
  )
}

// Top Debtors Report Component
const TopDebtorsReport = ({ data, loading }) => {
  if (loading) {
    return <Skeleton variant="rectangular" height={400} />
  }

  return (
    <Box>
      {/* Summary */}
      {data?.summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700} color="warning.main">
                  {data.summary.totalClients}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Clientes con Deuda
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={700} color="error.main">
                  {formatCurrency(data.summary.totalDebt)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Deuda Total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Chart */}
      <TopDebtorsChart 
        data={data?.debtors || []}
        loading={loading}
        title="Ranking de Clientes Deudores"
      />
    </Box>
  )
}

export default ReportsPage
